import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { updateAccountLedger } from '../../api/accounts';
import styles from './AccountLedgerDialog.module.css';

// Mark an account Personal or Business (categorization segments, Phase 2).
// The backend (PATCH /accounts/<id>/ledger) stamps the choice as a human
// decision, moves a bank/card account onto that ledger's chart, and
// re-categorizes the account's transactions no one has reviewed. An investment
// account stays on the investment chart; the choice only says whether a
// business owns it (its income then rolls into the measured self-employment
// line on the DRFA instead of the personal lines).

const OPTIONS = [
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'BUSINESS', label: 'Business' },
];

export const accountLedgerValue = (account) =>
  (account?.account_category === 'BUSINESS' || account?.ledger_segment === 'BUSINESS') ? 'BUSINESS' : 'PERSONAL';

export const accountLedgerLabel = (account) =>
  (accountLedgerValue(account) === 'BUSINESS' ? 'Business' : 'Personal');

// Why the account is on its ledger now (categorization segments, Phase 3).
// TITLE: titled to a business entity, so it became a business account
// automatically. HUMAN: someone chose it here. A choice here always wins over
// the title.
export const accountLedgerBasisNote = (account) => {
  const basis = account?.ledger_segment_basis;
  if (basis === 'TITLE') {
    const owners = (account?.title || [])
      .filter((t) => t?.kind === 'Business' && t?.display_name)
      .map((t) => t.display_name);
    const by = owners.length ? `: titled to ${owners.join(', ')}` : '';
    return `Business because of this account’s title${by}. Choosing Personal here overrides the title.`;
  }
  if (basis === 'HUMAN') {
    return 'Set by a person on this screen. New statements and title changes will not change it.';
  }
  return null;
};

export default function AccountLedgerDialog({ open, account, onClose, onSaved }) {
  const current = accountLedgerValue(account);
  const isInvestment = account?.ledger_segment === 'INVESTMENT';
  const basisNote = accountLedgerBasisNote(account);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValue(current);
      setError(null);
    }
  }, [open, current]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateAccountLedger(account.id, value);
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError('The change could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Personal or business account</DialogTitle>
      <DialogContent>
        <p className={styles.subtitle}>
          {isInvestment
            ? 'This investment account keeps its investment categories. Marking it Business moves its income off the personal lines of the Financial Affidavit and into the business owner’s self-employment line.'
            : 'Business accounts use business categories, and their income and expenses roll into the self-employment line of the Financial Affidavit instead of the personal lines. Changing this re-categorizes the account’s transactions that no one has reviewed; transactions a person categorized keep their category.'}
        </p>
        {basisNote && <p className={styles.basis}>{basisNote}</p>}
        <RadioGroup
          aria-label="Account ledger"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        >
          {OPTIONS.map((o) => (
            <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
          ))}
        </RadioGroup>
        {error && <div className={styles.error} role="alert">{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving || value === current}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
